import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { X, Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { computeLoadGross, nextInvoiceNumber, invoiceChargeLines } from '@/lib/invoiceHelpers';

const TERMS = ['Due on receipt', 'Net 15', 'Net 30', 'Net 45', 'Net 60'];
const termDays = (t) => ({ 'Net 15': 15, 'Net 30': 30, 'Net 45': 45, 'Net 60': 60 }[t] ?? 0);
const addDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
};
const money = (n) => '$' + (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AddInvoiceModal({ onClose, onCreated, initialLoadId, invoice }) {
  const isEdit = !!invoice;
  const [loads, setLoads] = useState([]);
  const [selectedLoadId, setSelectedLoadId] = useState(invoice?.load_id || '');
  const [form, setForm] = useState({
    customer_name: invoice?.customer_name || '',
    customer_email: invoice?.customer_email || '',
    customer_email_cc: (invoice?.customer_email_cc || []).join(', '),
    customer_address: invoice?.customer_address || '',
    custom_invoice_number: invoice?.custom_invoice_number || '',
    notes: invoice?.notes || '',
    invoiced_date: invoice?.invoiced_date || new Date().toISOString().split('T')[0],
    payment_terms: invoice?.payment_terms || 'Net 30',
  });
  // Itemized charges the PDF prints. Stored on the invoice once edited;
  // until then they're derived from the linked load's fees.
  const toRows = (lines) => lines.map(l => ({ label: l.label, amount: String(l.amount) }));
  const [lineItems, setLineItems] = useState(() => {
    if (invoice?.line_items?.length) return toRows(invoice.line_items);
    return toRows(invoiceChargeLines(null, invoice?.total_amount));
  });
  const [saving, setSaving] = useState(false);
  const itemsTotal = lineItems.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const setItem = (i, k, v) => setLineItems(rows => rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const addItem = () => setLineItems(rows => [...rows, { label: '', amount: '' }]);
  const removeItem = (i) => setLineItems(rows => rows.filter((_, idx) => idx !== i));

  // Editing an invoice with no saved line items: itemize from its load.
  useEffect(() => {
    if (!isEdit || invoice.line_items?.length || !invoice.load_id) return;
    client.entities.Load.filter({ id: invoice.load_id }).then(rows => {
      if (rows[0]) setLineItems(toRows(invoiceChargeLines(rows[0], invoice.total_amount)));
    }).catch(() => {});
  }, [isEdit, invoice?.id]);

  // Pulls the load's own customer name/email + computed amount onto the
  // form -- shared by manual selection and the initialLoadId auto-select
  // below so a load opened via "Create Invoice" from the Loads tab
  // (AdminPanel's InvoiceDropdown) arrives fully filled in, not just with
  // the amount.
  const fillFromLoad = (load) => {
    const amount = computeLoadGross(load);
    setForm(f => ({
      ...f,
      customer_name: load.customer_name || f.customer_name,
      customer_email: load.customer_email || f.customer_email,
    }));
    const rows = toRows(invoiceChargeLines(load, amount));
    if (rows.length) setLineItems(rows);
  };

  useEffect(() => {
    // Editing an existing invoice doesn't touch the load-link picker --
    // the load relationship (and the settlement-batch side effect below)
    // is set once at creation; re-fetching 200 loads and re-running
    // fillFromLoad here would also clobber the prefilled edit values.
    if (isEdit) return;
    client.entities.Load.list('-delivery_date', 200).then(all => {
      const delivered = all.filter(l => l.status === 'Delivered' || l.status === 'Invoiced');
      setLoads(delivered);
      if (initialLoadId) {
        const load = delivered.find(l => l.id === initialLoadId);
        if (load) {
          setSelectedLoadId(load.id);
          fillFromLoad(load);
        }
      }
    });
  }, [initialLoadId, isEdit]);

  const handleLoadSelect = (loadId) => {
    setSelectedLoadId(loadId);
    if (!loadId) {
      setForm(f => ({ ...f, customer_name: '', customer_email: '' }));
      setLineItems([{ label: 'Freight Services', amount: '' }]);
      return;
    }
    const load = loads.find(l => l.id === loadId);
    if (load) fillFromLoad(load);
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const buildFields = () => {
    const items = lineItems
      .map(r => ({ label: r.label.trim() || 'Charge', amount: parseFloat(r.amount) || 0 }))
      .filter(r => r.amount !== 0);
    const cc = form.customer_email_cc.split(/[,;\s]+/).map(e => e.trim()).filter(Boolean);
    const days = termDays(form.payment_terms);
    return {
      amount: items.reduce((s, r) => s + r.amount, 0),
      fields: {
        custom_invoice_number: form.custom_invoice_number || undefined,
        customer_name: form.customer_name,
        customer_email: form.customer_email || undefined,
        customer_email_cc: cc,
        customer_address: form.customer_address || undefined,
        invoiced_date: form.invoiced_date,
        payment_terms: form.payment_terms,
        due_date: addDays(form.invoiced_date, days),
        line_items: items,
        notes: form.notes || undefined,
      },
    };
  };

  const handleSave = async () => {
    const { amount, fields } = buildFields();
    if (!form.customer_name || !(amount > 0)) {
      toast.error('Customer name and at least one charge are required.');
      return;
    }
    setSaving(true);

    if (isEdit) {
      // Re-derive balance_due from whatever's actually been paid so far
      // (sum of the payments log EnterPaymentModal writes), rather than
      // just overwriting it with the new total -- otherwise editing the
      // amount on a partially-paid invoice would silently wipe out a
      // recorded payment's effect.
      const amountPaid = (invoice.payments || []).reduce((s, p) => s + (p.amount || 0), 0);
      const newBalance = Math.max(0, amount - amountPaid);
      await client.entities.Invoice.update(invoice.id, {
        ...fields,
        total_amount: amount,
        balance_due: newBalance,
        // Only actually flips a Paid invoice back to owing money if the
        // edited total now exceeds what's been paid -- a same-or-smaller
        // total on an already-Paid invoice stays Paid.
        ...(newBalance > 0 && invoice.status === 'Paid' ? { status: amountPaid > 0 ? 'Partial' : 'Unpaid' } : {}),
      });
      toast.success('Invoice updated.');
      setSaving(false);
      onCreated();
      onClose();
      return;
    }

    const allInvoices = await client.entities.Invoice.list('-created_date', 1000);
    const invoiceNumber = nextInvoiceNumber(allInvoices);
    const selectedLoad = selectedLoadId ? loads.find(l => l.id === selectedLoadId) : null;
    await client.entities.Invoice.create({
      ...fields,
      invoice_number: invoiceNumber,
      load_id: selectedLoad?.id || undefined,
      load_number: selectedLoad?.load_number || undefined,
      total_amount: amount,
      balance_due: amount,
      status: 'Unpaid',
    });
    if (selectedLoad && selectedLoad.status === 'Delivered') {
      await client.entities.Load.update(selectedLoad.id, { status: 'Invoiced' });
      // Move the driver's settlement into the payroll batch now that the
      // load has been billed to the customer -- previously this only
      // happened through a manual "Review & Approve" -> "Add to Batch"
      // click in the Driver Settlement tab, so a settlement could sit
      // with no visible status or action (new settlements have no
      // status set at all -- see ActiveLoad.jsx) and never actually
      // reach payroll even though the invoice had already gone out.
      try {
        const linkedSettlements = await client.entities.Settlement.filter({ load_id: selectedLoad.id });
        await Promise.all(linkedSettlements
          .filter(s => s.status !== 'Paid' && s.status !== 'In Batch')
          .map(s => client.entities.Settlement.update(s.id, { status: 'In Batch' })));
      } catch (e) {
        console.error('Failed to move settlement into payroll batch:', e);
      }
    }
    toast.success('Invoice created.');
    setSaving(false);
    onCreated();
    onClose();
  };

  const inputCls = "w-full px-3 py-2 rounded-lg bg-muted border border-border text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50";

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl my-8 p-6 space-y-4 z-10 bg-background border border-border rounded-2xl shadow-lg">
        <div className="flex items-center justify-between">
          <h2 className="font-space font-semibold text-foreground text-lg">{isEdit ? `Edit Invoice ${invoice.invoice_number}` : 'Add Invoice'}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>

        {/* Link to load -- fixed at creation, not editable here (see the
            isEdit guard above the load-fetch effect) */}
        {isEdit ? (
          invoice.load_number && (
            <p className="text-xs text-muted-foreground">Linked to Load #{invoice.load_number}</p>
          )
        ) : (
          <div>
            <label className="text-xs text-muted-foreground block mb-1.5">Link to Delivered / Invoiced Load (Optional)</label>
            <select
              value={selectedLoadId}
              onChange={e => handleLoadSelect(e.target.value)}
              className={inputCls}
            >
              <option value="" className="bg-background text-muted-foreground">— None (manual invoice) —</option>
              {loads.map(l => (
                <option key={l.id} value={l.id} className="bg-background">
                  #{l.load_number} — {l.origin && l.destination ? `${l.origin} → ${l.destination} — ` : ''}${l.rate?.toLocaleString()} [{l.status}]
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground block mb-1.5">Customer / Broker Name *</label>
            <input value={form.customer_name} onChange={e => set('customer_name', e.target.value)} placeholder="e.g. Echo Global Logistics" className={inputCls} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1.5">Customer Email</label>
            <input type="email" value={form.customer_email} onChange={e => set('customer_email', e.target.value)} placeholder="billing@broker.com" className={inputCls} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground block mb-1.5">CC Emails (comma separated)</label>
            <input value={form.customer_email_cc} onChange={e => set('customer_email_cc', e.target.value)} placeholder="ap@broker.com, ops@broker.com" className={inputCls} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1.5">Broker Ref / PO # (Optional)</label>
            <input value={form.custom_invoice_number} onChange={e => set('custom_invoice_number', e.target.value)} placeholder="e.g. BROKER-4455" className={inputCls} />
          </div>
        </div>

        <div>
          <label className="text-xs text-muted-foreground block mb-1.5">Billing Address (prints under Bill To)</label>
          <textarea value={form.customer_address} onChange={e => set('customer_address', e.target.value)} rows={2} className={cn(inputCls, "resize-none")} placeholder={"123 Main St\nChicago, IL 60601"} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground block mb-1.5">Invoiced Date</label>
            <input type="date" value={form.invoiced_date} onChange={e => set('invoiced_date', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1.5">Payment Terms</label>
            <select value={form.payment_terms} onChange={e => set('payment_terms', e.target.value)} className={inputCls}>
              {TERMS.map(t => <option key={t} value={t} className="bg-background">{t}</option>)}
            </select>
            {form.invoiced_date && (
              <p className="text-[11px] text-muted-foreground mt-1">Due {addDays(form.invoiced_date, termDays(form.payment_terms))}</p>
            )}
          </div>
        </div>

        {/* Itemized charges -- exactly what prints on the PDF */}
        <div>
          <label className="text-xs text-muted-foreground block mb-1.5">Charges *</label>
          <div className="space-y-2">
            {lineItems.map((r, i) => (
              <div key={i} className="flex items-center gap-2">
                <input value={r.label} onChange={e => setItem(i, 'label', e.target.value)} placeholder="Line Haul, Detention, Unloading…" className={inputCls} />
                <input type="number" value={r.amount} onChange={e => setItem(i, 'amount', e.target.value)} placeholder="0.00" className={cn(inputCls, "w-32 text-right")} />
                <button type="button" onClick={() => removeItem(i)} className="p-2 rounded-lg text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors flex-shrink-0">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
          <button type="button" onClick={addItem} className="mt-2 flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 font-medium">
            <Plus className="w-3.5 h-3.5" /> Add charge
          </button>
          <div className="mt-3 pt-3 border-t border-border space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span className="font-semibold text-foreground">{money(itemsTotal)}</span></div>
            {isEdit && (invoice.payments || []).length > 0 && (
              <>
                {(invoice.payments || []).map((p, i) => (
                  <div key={i} className="flex justify-between text-xs text-muted-foreground">
                    <span>Payment {p.date}{p.reference ? ` (${p.reference})` : ''}</span><span>-{money(p.amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between"><span className="text-muted-foreground">Balance Due</span>
                  <span className="font-semibold text-foreground">{money(Math.max(0, itemsTotal - (invoice.payments || []).reduce((s, p) => s + (p.amount || 0), 0)))}</span>
                </div>
              </>
            )}
          </div>
        </div>

        <div>
          <label className="text-xs text-muted-foreground block mb-1.5">Notes (prints on the invoice)</label>
          <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} className={cn(inputCls, "resize-none")} placeholder="Remit via ACH to…, thank-you note, etc." />
        </div>

        <div className="flex gap-3 pt-1">
          <Button onClick={handleSave} disabled={saving} className="flex-1 bg-primary text-primary-foreground">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : (isEdit ? 'Save Changes' : 'Create Invoice')}
          </Button>
          <Button onClick={onClose} variant="outline" className="bg-background border-border hover:bg-muted">Cancel</Button>
        </div>
      </div>
    </div>
  );
}